import React, { useState } from 'react';
import {
    Card,
    Space,
    Typography,
    Button,
    Tabs,
    Descriptions,
    Spin,
    Result,
    message,
    Modal,
    Row,
    Col,
} from 'antd';
import {
    ArrowLeftOutlined,
    StopOutlined,
    CheckCircleOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetFlaggedResponseDetailQuery,
    useRejectFlaggedResponseMutation,
    useConfirmCleanFlaggedResponseMutation,
    useRescoreFlaggedResponseMutation,
} from '../../../services/nestjs-base-api';
import { QualityScoreDisplay } from '../../components/response-integrity/QualityScoreDisplay';
import { StatusBadge } from '../../components/response-integrity/StatusBadge';
import { DetectorSignalsTable } from '../../components/response-integrity/DetectorSignalsTable';
import { AnswersTable } from '../../components/response-integrity/AnswersTable';
import { EvidencePackViewer } from '../../components/response-integrity/EvidencePackViewer';
import { RejectionDialog } from '../../components/response-integrity/RejectionDialog';
import { BrandEquityComparison } from '../../components/response-integrity/BrandEquityComparison';

const { Title, Text } = Typography;

export const ResponseDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const numericId = id ? parseInt(id, 10) : 0;
    const navigate = useNavigate();
    const intl = useIntl();
    const { user } = useAuth();

    const { data, isLoading, isError } = useGetFlaggedResponseDetailQuery(
        { jwtToken: user?.jwtToken || '', id: numericId },
        { skip: !numericId },
    );

    const [reject, { isLoading: rejecting }] = useRejectFlaggedResponseMutation();
    const [confirmClean, { isLoading: confirming }] = useConfirmCleanFlaggedResponseMutation();
    const [rescore, { isLoading: rescoring }] = useRescoreFlaggedResponseMutation();

    const [rejectOpen, setRejectOpen] = useState(false);

    const handleReject = async (reason: string, notes?: string) => {
        try {
            await reject({
                jwtToken: user?.jwtToken || '',
                id: numericId,
                reason,
                notes,
            }).unwrap();
            message.success(intl.formatMessage({ id: 'responseIntegrity.toast.rejected' }));
            setRejectOpen(false);
        } catch (e) {
            message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
        }
    };

    const handleConfirmClean = () => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'responseIntegrity.confirm.confirmCleanTitle' }),
            content: intl.formatMessage({ id: 'responseIntegrity.confirm.confirmCleanBody' }),
            okText: intl.formatMessage({ id: 'responseIntegrity.actions.confirmClean' }),
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk: async () => {
                try {
                    await confirmClean({
                        jwtToken: user?.jwtToken || '',
                        id: numericId,
                    }).unwrap();
                    message.success(
                        intl.formatMessage({ id: 'responseIntegrity.toast.confirmedClean' }),
                    );
                } catch (e) {
                    message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
                }
            },
        });
    };

    const handleRescore = () => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'responseIntegrity.confirm.rescoreTitle' }),
            content: intl.formatMessage({ id: 'responseIntegrity.confirm.rescoreBody' }),
            okText: intl.formatMessage({ id: 'responseIntegrity.actions.rescore' }),
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk: async () => {
                try {
                    await rescore({
                        jwtToken: user?.jwtToken || '',
                        id: numericId,
                    }).unwrap();
                    message.success(intl.formatMessage({ id: 'responseIntegrity.toast.rescored' }));
                } catch (e) {
                    message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
                }
            },
        });
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }
    if (isError || !data) {
        return (
            <Result
                status="404"
                title="Response not found"
                extra={
                    <Button onClick={() => navigate('/admin/response-integrity')}>
                        <FormattedMessage id="responseIntegrity.actions.back" />
                    </Button>
                }
            />
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space>
                    <Link to="/admin/response-integrity">
                        <Button icon={<ArrowLeftOutlined />}>
                            <FormattedMessage id="responseIntegrity.actions.back" />
                        </Button>
                    </Link>
                </Space>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card>
                            <Space direction="vertical" align="center" style={{ width: '100%' }}>
                                <Title level={4} style={{ margin: 0 }}>
                                    <FormattedMessage
                                        id="responseIntegrity.detail.title"
                                        values={{ id: data.id }}
                                    />
                                </Title>
                                <QualityScoreDisplay
                                    score={data.qualityScore}
                                    tier={data.qualityTier}
                                    size="large"
                                />
                                <StatusBadge status={data.rejectionStatus} />
                                <Space style={{ marginTop: 12 }} wrap>
                                    <Button
                                        danger
                                        icon={<StopOutlined />}
                                        loading={rejecting}
                                        onClick={() => setRejectOpen(true)}
                                        disabled={data.rejectionStatus === 'rejected'}
                                    >
                                        <FormattedMessage id="responseIntegrity.actions.reject" />
                                    </Button>
                                    <Button
                                        icon={<CheckCircleOutlined />}
                                        loading={confirming}
                                        onClick={handleConfirmClean}
                                        disabled={data.rejectionStatus === 'confirmed_clean'}
                                    >
                                        <FormattedMessage id="responseIntegrity.actions.confirmClean" />
                                    </Button>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        loading={rescoring}
                                        onClick={handleRescore}
                                    >
                                        <FormattedMessage id="responseIntegrity.actions.rescore" />
                                    </Button>
                                </Space>
                            </Space>
                        </Card>
                    </Col>
                    <Col xs={24} md={16}>
                        <Card
                            title={
                                <FormattedMessage id="responseIntegrity.detail.surveyContext" />
                            }
                        >
                            <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.table.survey' })}>
                                    {data.surveyName || `#${data.surveyId}`}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.table.brand' })}>
                                    {data.brandName || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.submittedAt' })}>
                                    {dayjs(data.submittedAt).format('YYYY-MM-DD HH:mm:ss')}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.startedAt' })}>
                                    {data.startedAt ? dayjs(data.startedAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.ipAddress' })}>
                                    <Text code>{data.ipAddress || '—'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.fingerprint' })}>
                                    <Text code style={{ fontSize: 11 }}>
                                        {data.fingerprintHash
                                            ? `${data.fingerprintHash.slice(0, 16)}…`
                                            : '—'}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.panelPartner' })}>
                                    {data.panelPartner || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.panelSession' })}>
                                    <Text code style={{ fontSize: 11 }}>
                                        {data.panelSessionId || '—'}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.participant' })}>
                                    {data.participantEmail || data.participantIdentifier || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.userAgent' })} span={2}>
                                    <Text style={{ fontSize: 12 }}>{data.userAgent || '—'}</Text>
                                </Descriptions.Item>
                                {data.rejectionStatus && (
                                    <>
                                        <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.rejectedBy' })}>
                                            {data.rejectedBy ? `User #${data.rejectedBy}` : '—'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={intl.formatMessage({ id: 'responseIntegrity.detail.rejectedAt' })}>
                                            {data.rejectedAt
                                                ? dayjs(data.rejectedAt).format('YYYY-MM-DD HH:mm:ss')
                                                : '—'}
                                        </Descriptions.Item>
                                        {data.rejectionReason && (
                                            <Descriptions.Item
                                                label={intl.formatMessage({ id: 'responseIntegrity.detail.rejectionReason' })}
                                                span={2}
                                            >
                                                <FormattedMessage
                                                    id={`responseIntegrity.reasons.${data.rejectionReason}`}
                                                    defaultMessage={data.rejectionReason}
                                                />
                                            </Descriptions.Item>
                                        )}
                                    </>
                                )}
                            </Descriptions>
                        </Card>
                    </Col>
                </Row>

                {data.brandId && (
                    <BrandEquityComparison brandId={data.brandId} />
                )}

                <Card styles={{ body: { padding: 16 } }}>
                    <Tabs
                        defaultActiveKey="signals"
                        items={[
                            {
                                key: 'signals',
                                label: <FormattedMessage id="responseIntegrity.detail.tabs.signals" />,
                                children: <DetectorSignalsTable signals={data.signals || []} />,
                            },
                            {
                                key: 'evidence',
                                label: <FormattedMessage id="responseIntegrity.detail.tabs.evidence" />,
                                children: <EvidencePackViewer evidence={data.evidencePack} />,
                            },
                            {
                                key: 'answers',
                                label: <FormattedMessage id="responseIntegrity.detail.tabs.answers" />,
                                children: <AnswersTable answers={data.answers || []} />,
                            },
                        ]}
                    />
                </Card>
            </Space>

            <RejectionDialog
                open={rejectOpen}
                submitting={rejecting}
                onCancel={() => setRejectOpen(false)}
                onSubmit={handleReject}
            />
        </div>
    );
};
